import { getExportHistory } from '@/services/call-log-service/call-log-service';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebsocket } from '../use-websocket/use-websocket';
import {
  ExportHistory,
  ExportHistoryResponse,
} from '@/services/call-log-service/call-log-service.types';

export type ExportWatchOutcome = 'not-started' | 'stuck' | null;

interface useExportHistoryProps {
  page: number;
  items_per_page: number;
  // The give-up deadline keeps running even while polling is suppressed.
  isActive?: boolean;
}

const POLL_INTERVAL_MS = 5_000;
const ROW_CREATION_GRACE_MS = 3 * 60_000;

// Anchored to created_at, not to arm time, so an expired watch cannot re-arm.
const WATCH_TIMEOUT_MS = 12 * 60_000;

const MAX_POLL_FAILURES = 3;

const isUnfinished = (report: ExportHistoryResponse) => report.status === 'pending';

const rowDeadline = (report: ExportHistoryResponse) =>
  Date.parse(report.created_at) + WATCH_TIMEOUT_MS;

const couldStillBeRunning = (report: ExportHistoryResponse, now: number) => {
  const deadline = rowDeadline(report);
  return isUnfinished(report) && Number.isFinite(deadline) && deadline > now;
};

// Guards against clock skew: a row this watch just saw created must not read as
// already expired.
const boundedDeadline = (report: ExportHistoryResponse, now: number) => {
  const deadline = rowDeadline(report);
  const ceiling = now + WATCH_TIMEOUT_MS;
  if (!Number.isFinite(deadline) || deadline <= now) return ceiling;
  return Math.min(deadline, ceiling);
};

const newest = (reports: ExportHistoryResponse[]) =>
  reports.reduce<ExportHistoryResponse | null>(
    (best, row) => (best && best.id > row.id ? best : row),
    null
  );

interface ExportWatch {
  // Separates the row this export creates from rows already there.
  sinceId: number | null;
  rowId: number | null;
  doneBy: number;
  // errorUpdateCount is per query key, so the baseline travels with the key it
  // was taken from. Comparing across keys counts another list's failures.
  errorsKey: string;
  errorsAtArm: number;
}

export const useExportHistory = ({
  page,
  items_per_page,
  isActive = true,
}: useExportHistoryProps) => {
  const queryClient = useQueryClient();
  const [watch, setWatch] = useState<ExportWatch | null>(null);
  const [exportWatchTimedOut, setExportWatchTimedOut] = useState<ExportWatchOutcome>(null);
  const [exportWatchFailed, setExportWatchFailed] = useState(false);
  const seenFirstLoad = useRef(false);
  const displayedKey = JSON.stringify(ReactQueryKeys.EXPORT_HISTORY({ items_per_page, page }));

  const {
    data: callLogExportHistoryData,
    error,
    isPending,
    refetch,
    errorUpdateCount,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ReactQueryKeys.EXPORT_HISTORY({ items_per_page, page }),
    queryFn: () => getExportHistory({ items_per_page, page }),
    staleTime: Infinity,
    // The poll is the retry.
    retry: false,
    // refetchOnWindowFocus is off app-wide and the drawer never remounts, so a
    // hidden tab has nothing else to refresh with.
    refetchIntervalInBackground: true,
    // Must stay a stable number: react-query restarts the interval on any change.
    refetchInterval: (query) => {
      if (!watch || !isActive) return false;
      if (
        watch.errorsKey === JSON.stringify(query.queryKey) &&
        query.state.errorUpdateCount - watch.errorsAtArm >= MAX_POLL_FAILURES
      ) {
        return false;
      }

      if (watch.rowId === null) {
        return Date.now() < watch.doneBy ? POLL_INTERVAL_MS : false;
      }
      const row = (query.state.data?.reports ?? []).find((r) => r.id === watch.rowId);
      // Not on the displayed page: pause, do not stand down. Paging back resumes.
      if (!row) return false;
      return isUnfinished(row) ? POLL_INTERVAL_MS : false;
    },
  });

  const watchExport = useCallback(() => {
    // Page 1, not the displayed page: a baseline from a later page could never
    // be exceeded.
    const pageOneKey = ReactQueryKeys.EXPORT_HISTORY({ items_per_page, page: 1 });
    const cached = queryClient.getQueryData<ExportHistory>(pageOneKey);
    const ids = cached?.reports.map((report) => report.id) ?? [];
    setExportWatchTimedOut(null);
    setExportWatchFailed(false);
    setWatch({
      sinceId: ids.length ? Math.max(...ids) : null,
      rowId: null,
      doneBy: Date.now() + ROW_CREATION_GRACE_MS,
      // Page 1's own count, not the displayed page's: the caller switches to page
      // 1 alongside this, so that is the query this watch will be polling.
      errorsKey: JSON.stringify(pageOneKey),
      errorsAtArm: queryClient.getQueryState(pageOneKey)?.errorUpdateCount ?? 0,
    });
    queryClient.refetchQueries({ queryKey: pageOneKey });
  }, [queryClient, items_per_page]);

  // Tracks one row, never "any unfinished row on the page".
  useEffect(() => {
    const reports = callLogExportHistoryData?.reports;
    if (!reports) return;
    const now = Date.now();
    const firstLoad = !seenFirstLoad.current;
    seenFirstLoad.current = true;

    if (!watch) {
      // Only the first load may adopt a row it did not start, which is a reload
      // taken mid-export. Later a stand-down would hand the watch to an
      // unrelated export and warn about one the user never triggered.
      if (!firstLoad) return;
      const candidate = reports.find((report) => couldStillBeRunning(report, now));
      if (candidate) {
        setWatch({
          sinceId: null,
          rowId: candidate.id,
          doneBy: boundedDeadline(candidate, now),
          errorsKey: displayedKey,
          errorsAtArm: errorUpdateCount,
        });
      }
      return;
    }

    if (watch.rowId === null) {
      const row = newest(
        reports.filter((report) =>
          watch.sinceId === null ? isUnfinished(report) : report.id > watch.sinceId
        )
      );
      if (!row) return;
      if (!isUnfinished(row)) {
        setWatch(null);
        return;
      }
      setWatch({ ...watch, rowId: row.id, doneBy: boundedDeadline(row, now) });
      return;
    }

    const row = reports.find((report) => report.id === watch.rowId);
    if (row && !isUnfinished(row)) setWatch(null);
  }, [dataUpdatedAt, watch, callLogExportHistoryData, errorUpdateCount]);

  // Needs its own timer: once the poll predicate returns false, nothing else
  // would notice the deadline pass.
  useEffect(() => {
    if (!watch) return;
    const delay = watch.doneBy - Date.now();
    if (!Number.isFinite(delay)) return;

    const giveUp = () => {
      // Silent while nothing displays the list: polling was suppressed, so the
      // cached row may be stale.
      if (isActive) setExportWatchTimedOut(watch.rowId === null ? 'not-started' : 'stuck');
      setWatch(null);
    };

    if (delay <= 0) {
      giveUp();
      return;
    }
    const timeoutId = setTimeout(giveUp, delay);
    return () => clearTimeout(timeoutId);
  }, [watch, isActive]);

  // react-query keeps data and dataUpdatedAt across an error, so the predicate
  // would otherwise poll on forever off the last successful snapshot.
  useEffect(() => {
    if (!watch || watch.errorsKey !== displayedKey) return;
    if (errorUpdateCount - watch.errorsAtArm < MAX_POLL_FAILURES) return;
    setExportWatchFailed(true);
    setWatch(null);
  }, [errorUpdateCount, watch, displayedKey]);

  // Resync on reconnect: a `report` frame dropped while the socket was down is not
  // replayed, and polling only runs while a watch is armed.
  useWebsocket<{ report: ExportHistoryResponse }>('ReportChannel', ({ report }) => {
    if (!report) return;

    queryClient.setQueryData(
      ReactQueryKeys.EXPORT_HISTORY({ items_per_page, page }),
      (oldData: ExportHistory | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          reports: oldData.reports.map((item) =>
            item.id === report.id ? { ...item, ...report } : item
          ),
        };
      }
    );
  }, { onReconnect: refetch });

  return {
    callLogExportHistoryData,
    error,
    isPending,
    refetch,
    watchExport,
    exportWatchTimedOut,
    exportWatchFailed,
  };
};
